<template>
	<v-row dense style="max-height: calc(100vh - 60px) !important" class="pa-2 ma-0">
		<v-dialog v-model="dialog" max-width="350">
			<v-card>
				<v-card-title
					class="font-size16 white--text my-0 py-0"
					style="background-color: #a51717; height: 40px !important">
					{{ $t('create-resident') }}
				</v-card-title>
				<v-card-text class="font-size15 d-flex flex-column mt-2">
					{{ $t('are-you-sure-the-resident') }} "{{ resident?.residentFirstName }} {{ resident?.residentLastName }}"
					{{ $t('is-not-mapped') }} ?
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn small @click="dialog = false">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn class="white--text" small color="chips" @click="confirmCreate">
						{{ $t('create') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="deleteDialog" max-width="350">
			<v-card>
				<v-card-title
					class="font-size16 white--text my-0 py-0"
					style="background-color: #a51717; height: 40px !important">
					{{ $t('delete-pcc-resident-mapping') }}
				</v-card-title>
				<v-card-text class="font-size15 d-flex flex-column mt-2">
					{{
						`${$t('are-you-sure-you-want-to-delete')} ${patientToDelete?.firstName} ${patientToDelete?.lastName}
						${$t('mapping')}?`
					}}
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn small @click="deleteDialog = false">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn class="white--text" small color="chips" @click="confirmDeletion" :disabled="loading">
						{{ $t('delete') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="createResidentDialog" max-width="1000">
			<v-card :height="mapDialogStep === 2 ? '80vh' : ''">
				<v-card-title
					class="font-size16 white--text my-0 py-0"
					style="background-color: #a51717; height: 40px !important; display: flex; justify-content: space-between">
					{{
						mapDialogStep === 1
							? `${$t('create-pcc-resident-mapping')} ${$t('for')} ${userDetails.firstName} ${userDetails.lastName}`
							: `${$t('onboard-practitioners-for')} ${resident.firstName} ${resident.lastName} ${$t('from-pcc')} (${$t(
									'optional',
							  )})`
					}}
					<v-icon @click="createResidentDialog = false" class="white-icon"> mdi-close </v-icon>
				</v-card-title>
				<v-card-text class="font-size15 d-flex flex-column pa-4">
					<v-row dense>
						<v-col cols="12" v-if="mapDialogStep === 1">
							<v-form ref="form" lazy-validation>
								<v-row dense>
									<v-col cols="6">
										<!-- Name -->
										<v-text-field
											outlined
											hide-details
											dense
											v-model="userDetails.firstName"
											:label="$t('name')"
											disabled />
									</v-col>
									<v-col cols="6">
										<!-- Surname -->
										<v-text-field
											outlined
											hide-details
											dense
											v-model="userDetails.lastName"
											:label="$t('surname')"
											disabled />
									</v-col>
									<v-col cols="6">
										<!-- Birthday -->
										<BirthdatePicker :inputValue.sync="birthdate" disabled />
									</v-col>
									<v-col cols="6">
										<!-- Gender -->
										<v-select
											outlined
											hide-details
											dense
											:label="$t('gender')"
											:items="patients.gender"
											v-model="userDetails.gender"
											:menu-props="{ offsetY: true }"
											disabled>
											<template #item="{ item }">
												{{ item.text }}
											</template>
										</v-select>
									</v-col>
									<v-col cols="12">
										<!-- Prescribing doctor -->
										<v-select
											outlined
											dense
											v-model="prescribingDoctor"
											:label="$t('prescribing-doctor')"
											:items="doctors"
											return-object
											:item-text="(a) => a.firstName + ' ' + a.lastName"
											item-value="id"
											:menu-props="{ maxHeight: 200, offsetY: true }"
											hide-details />
									</v-col>
									<!-- Select location dropdown -->
									<v-col cols="12">
										<v-select
											outlined
											hide-details
											dense
											:label="$t('select-location')"
											:items="userData.hospitalsAssignedData"
											item-text="hospitalName"
											return-object
											v-model="selectedLocation"
											@change="onLocationSelect"
											disabled>
											<template #item="{ item }">
												{{ item.hospitalName }}
											</template>
										</v-select>
									</v-col>
									<!-- Address -->
									<v-col cols="12">
										<v-text-field
											ref="address"
											v-model="patientAddress"
											:label="$t('address')"
											outlined
											hide-details
											dense
											disabled>
										</v-text-field>
									</v-col>
									<!-- City -->
									<v-col class="pa-1" cols="6">
										<v-text-field
											outlined
											hide-details
											dense
											:label="$t('city')"
											v-model="addressInfo.city"
											required
											disabled />
									</v-col>
									<!-- State -->
									<v-col class="pa-1" cols="6">
										<v-text-field
											outlined
											hide-details
											dense
											:label="$t('state')"
											v-model="addressInfo.state"
											required
											disabled />
									</v-col>
									<!-- Zip -->
									<v-col class="pa-1" cols="6">
										<v-text-field
											outlined
											hide-details
											dense
											:label="$t('zip')"
											v-model="addressInfo.zip"
											required
											disabled />
									</v-col>
									<!-- Country -->
									<v-col class="pa-1" cols="6">
										<v-text-field
											outlined
											hide-details
											dense
											:label="$t('country')"
											v-model="addressInfo.country"
											required
											disabled />
									</v-col>

									<v-col cols="12" v-if="!addressIsValid">
										<v-combobox
											:label="$t('time-zone')"
											dense
											hide-details="auto"
											outlined
											:rules="[(v) => !!v || $t('timeZoneSelect')]"
											v-model="timezone"
											item-text="timeZoneId"
											item-value="timeZoneId"
											:items="getTimeZone"
											required />
									</v-col>
									<template v-if="userData.isSequoiaUser === true || roleName === 'HOSPITAL_ADMIN'">
										<v-col class="pa-1 text-left" cols="4">
											<div>
												<span class="font-weight-bold color-black">{{ $t('enable-rpm') }}</span>
												<v-btn-toggle v-model="enableRPM" row active-class="chips white--text" mandatory class="white">
													<v-btn class="tabs-class" :value="true" :input-value="enableRPM" style="max-width: 50%">
														{{ $t('yes') }}
													</v-btn>
													<v-btn class="tabs-class" :value="false" :input-value="enableRPM" style="max-width: 50%">
														{{ $t('no') }}
													</v-btn>
												</v-btn-toggle>
											</div>
										</v-col>
										<v-col class="pa-1" cols="4">
											<div>
												<span class="font-weight-bold color-black">{{ $t('enable-rtm') }}</span>
												<v-btn-toggle v-model="enableRTM" row active-class="chips white--text" mandatory class="white">
													<v-btn class="tabs-class" :value="true" :input-value="enableRTM" style="max-width: 50%">
														{{ $t('yes') }}
													</v-btn>
													<v-btn class="tabs-class" :value="false" :input-value="enableRTM" style="max-width: 50%">
														{{ $t('no') }}
													</v-btn>
												</v-btn-toggle>
											</div>
										</v-col>
										<v-col class="pa-1 text-right" cols="4">
											<div>
												<span class="font-weight-bold color-black">{{ $t('enable-ccm') }}</span>
												<v-btn-toggle v-model="enableCCM" row active-class="chips white--text" mandatory class="white">
													<v-btn class="tabs-class" :value="true" :input-value="enableCCM" style="max-width: 50%">
														{{ $t('yes') }}
													</v-btn>
													<v-btn class="tabs-class" :value="false" :input-value="enableCCM" style="max-width: 50%">
														{{ $t('no') }}
													</v-btn>
												</v-btn-toggle>
											</div>
										</v-col>
									</template>
								</v-row>
							</v-form>
						</v-col>
						<v-col cols="12" v-else>
							<v-data-table
								class="data-table-outline"
								:items="getPractitionersResponse"
								dense
								outlined
								:headers="practitionersHeaders"
								height="calc(80vh - 133px)"
								:loading="loadingPractitionersHeaders">
								<template #item="{ item, index }">
									<tr :class="{ 'odd-row': index % 2 }">
										<td class="text-left">
											{{ item.title ? item.title + ' ' : '' }}{{ item.firstName }} {{ item.lastName }}
										</td>
										<td class="text-left">{{ item.cellPhone || item.officePhone }}</td>
										<td class="text-left">{{ item.providerType }}</td>
										<td class="text-left">
											<v-select
												v-if="!item.existsInPccUserMapping"
												:items="roles"
												dense
												required
												hide-details
												item-text="roleNameForDisplay"
												item-value="id"
												v-model="getPractitionersResponse[index].role"
												:menu-props="{ offsetY: true }" />
											<v-icon v-else size="20">mdi-alert-circle-outline</v-icon>
										</td>
										<td class="text-right">
											<v-btn
												v-if="!item.existsInPccUserMapping"
												dense
												x-small
												color="primary"
												@click="createMedicalStaffFromPcc(index)"
												:disabled="!getPractitionersResponse[index].role">
												{{ $t('create') }}
											</v-btn>
										</td>
									</tr>
								</template>
								<template #[`footer.prepend`]>
									<v-icon size="22" class="mr-1">mdi-alert-circle-outline</v-icon>
									<span style="color: #757575">{{ $t('already-onboarded') }}</span>
								</template>
							</v-data-table>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions class="pa-4 pt-0" v-if="mapDialogStep === 1">
					<v-spacer />
					<v-btn small @click="createResidentDialog = false">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn class="white--text" :disabled="isSaveButtonDisabled" small color="chips" @click="createPatient">
						{{ $t('create') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- TODO:  With Update Table -->
		<v-col
			cols="6"
			:style="{
				height: selectedPccUser !== null && selectedNotMappedUser !== null ? 'calc((100vh - 129px) / 2)' : '50%',
			}">
			<v-card elevation="5" height="100%" ref="pccUsersTable" @resize="updateDataTableHeight">
				<div class="d-flex justify-space-between elevation-3">
					<div class="d-flex align-center">
						<v-img :src="pccLogoBlack" class="ml-2" max-height="21" max-width="22" />
						<div class="text-left ml-2 font-weight-bold font-size16">
							{{ $t('unmapped-patients-from-pcc') }}
						</div>
					</div>
					<div>
						<v-text-field
							single-line
							outlined
							class="pa-1"
							prepend-inner-icon="mdi-magnify"
							hide-details
							dense
							v-model="searchPccPatient"
							:label="$t('search')" />
					</div>
				</div>
				<v-divider />
				<v-radio-group class="small ma-0 pa-0" v-model="selectedPccUser">
					<v-data-table
						:loading="loadingUsersNeedToBeMapped"
						:headers="pccHeaders"
						:items="items.usersNeedToBeMapped"
						:search="searchPccPatient"
						:height="tableHeight"
						fixed-header
						:footer-props="getDataTableFooterProps('residentMappingTable')"
						:items-per-page="-1"
						:no-data-text="$t('no-data-available')">
						<template #item="{ item, index }">
							<tr :class="getRowsForDataTable(index)">
								<v-tooltip color="primary" top>
									<template #activator="{ on, attrs }">
										<td style="height: 30px !important" class="table-font-size px-2 py-0" v-bind="attrs" v-on="on">
											<div class="d-flex">
												<v-img class="mr-2" :src="PatientBlack" max-width="13px" max-height="20px" />
												{{ item.residentFirstName }} {{ item.residentLastName }}
											</div>
										</td>
									</template>
									<span>{{ item?.residentStatusDescription || '' }}</span>
								</v-tooltip>
								<td style="height: 30px !important" class="table-font-size px-2 py-0">
									<v-chip label pill x-small color="primary" v-if="item.residentBirthDate">
										{{ calculateAge(item.residentBirthDate) }}
									</v-chip>
								</td>
								<td style="height: 30px !important" class="table-font-size px-2 py-0">
									<span v-if="item.residentBirthDate">
										{{ formatTimestamp(item.residentBirthDate?.split('T')[0]) }}
									</span>
									<span v-else class="red--text">{{ $t('missing-data') }}</span>
								</td>
								<td style="height: 30px !important" class="table-font-size px-2 py-0">
									<span v-if="item.residentGender">{{ $t(item.residentGender.toLowerCase()) }}</span>
									<span v-else class="red--text">{{ $t('missing-data') }}</span>
								</td>
								<td
									style="height: 30px !important"
									class="table-font-size pl-8 py-0 d-flex justify-center align-center">
									<v-tooltip bottom v-if="item.residentMapSuggestion?.score >= 5">
										<template #activator="{ on, attrs }">
											<div v-bind="attrs" v-on="on" class="d-flex align-center">
												<v-radio :value="item" @click="clickResidentNeedToBeMap(item)" />
											</div>
										</template>

										<!-- Tooltip content -->
										<div class="pa-2 text-left" style="max-width: 300px; white-space: normal">
											<strong>{{ $t('suggested-to-map-with') }}:</strong><br /><br />
											<span>{{ item.residentMapSuggestion.localUserFullName }}</span>
											<div>{{ $t('dob') }}: {{ item.residentMapSuggestion.localUserBirthdate }}</div>
											<div v-if="item.residentMapSuggestion.evidence?.length">
												<span>{{ $t('evidence') }}:</span>
												<ul class="ma-0 pa-0 pl-4">
													<li v-for="(ev, idx) in item.residentMapSuggestion.evidence" :key="idx">
														{{ ev }}
													</li>
												</ul>
											</div>
										</div>
									</v-tooltip>
									<v-btn
										v-else-if="item.residentGender === null || item.residentBirthDate === null"
										disabled
										x-small
										color="primary"
										class="text-capitalize">
										{{ $t('create') }}
									</v-btn>
									<v-btn v-else x-small color="primary" class="text-capitalize" @click="createResident(item)">
										{{ $t('create') }}
									</v-btn>
								</td>
							</tr>
						</template>
					</v-data-table>
				</v-radio-group>
			</v-card>
		</v-col>
		<!-- TODO:  Not Mapped Table -->
		<v-col
			cols="6"
			:style="{
				height: selectedPccUser !== null && selectedNotMappedUser !== null ? 'calc((100vh - 129px) / 2)' : '50%',
			}">
			<v-card elevation="5" height="100%">
				<div class="d-flex justify-space-between elevation-3">
					<div class="d-flex align-center">
						<v-img :src="logoDrawer" class="ml-2" max-height="21" max-width="22" />
						<div class="text-left ml-2 font-weight-bold font-size16">{{ $t('unmapped-patients') }}</div>
					</div>
					<div>
						<v-text-field
							single-line
							outlined
							class="py-1 px-1"
							prepend-inner-icon="mdi-magnify"
							hide-details
							dense
							v-model="searchNotMappedPatient"
							:label="$t('search')" />
					</div>
				</div>
				<v-divider />
				<v-radio-group v-model="selectedNotMappedUser" class="small ma-0 pa-0">
					<v-data-table
						:loading="loadingNotMappedUsers"
						:headers="notMappedPatientsHeaders"
						:items="items.notMappedUsers"
						:search="searchNotMappedPatient"
						:height="tableHeight"
						fixed-header
						:footer-props="getDataTableFooterProps('residentMappingTable')"
						:items-per-page="-1"
						:no-data-text="$t('no-data-available')">
						<template #item="{ item }">
							<tr
								:ref="(el) => setRowRef(item.id, el)"
								:class="{ 'highlight-row': highlightedId === item.id && selectedNotMappedUser }">
								<td style="height: 30px !important" class="table-font-size px-2 py-0">
									<div class="d-flex align-center">
										<div class="d-flex align-center" style="min-width: 80px">
											<v-chip class="mr-5" x-small hide-details :color="getColorStatus(item.status)">
												{{ getStatus(item.status) }}
											</v-chip>
										</div>
										<div>
											<span>{{ item.firstName }} {{ item.lastName }}</span>
										</div>
									</div>
								</td>
								<td style="height: 30px !important" class="table-font-size px-2 py-0">
									<v-chip label pill x-small color="primary"> {{ calculateAge(item.birthdate) }}</v-chip>
								</td>
								<td style="height: 30px !important" class="table-font-size px-2 py-0">
									{{ formatTimestamp(item.birthdate) }}
								</td>
								<td style="height: 30px !important" class="table-font-size px-2 py-0">
									{{ item.gender === 'F' ? $t('female') : $t('male') }}
								</td>
								<td style="height: 30px !important" class="table-font-size pl-8 py-0">
									<v-radio :value="item"></v-radio>
								</td>
							</tr>
						</template>
					</v-data-table>
				</v-radio-group>
			</v-card>
		</v-col>
		<v-col
			cols="12"
			class="py-2"
			v-if="selectedPccUser !== null && selectedNotMappedUser !== null"
			style="height: 52px">
			<v-btn @click="mapUser" color="primary2" block>
				<span style="text-transform: capitalize" class="font-size15 white--text">{{ $t('map-user') }}</span>
			</v-btn>
		</v-col>
		<!--   TODO: MAPPED USERS   -->
		<v-col
			cols="12"
			:style="{
				height: selectedPccUser !== null && selectedNotMappedUser !== null ? 'calc((100vh - 129px) / 2)' : '50%',
			}">
			<v-card elevation="5" height="100%">
				<div class="d-flex justify-space-between elevation-3">
					<div class="d-flex align-center">
						<v-img class="ml-2" :src="PatientBlack" max-width="13px" max-height="20px" />
						<div class="text-left ml-2 font-weight-bold font-size16">{{ $t('mapped-patients') }}</div>
					</div>
					<div>
						<v-text-field
							single-line
							outlined
							class="py-1 px-1"
							prepend-inner-icon="mdi-magnify"
							hide-details
							dense
							v-model="searchMappedPatient"
							:label="$t('search')" />
					</div>
				</div>
				<v-divider />
				<v-data-table
					:loading="loadingMappedUsers"
					:headers="staffHeaders"
					:items="items.mappedUsers"
					:search="searchMappedPatient"
					:height="tableHeight"
					fixed-header
					:footer-props="getDataTableFooterProps('residentMappingTable')"
					:items-per-page="-1"
					:no-data-text="$t('no-data-available')">
					<template #item="{ item, index }">
						<tr :class="getRowsForDataTable(index)" @click="addResidentPractitioners(item)">
							<td style="height: 30px !important" class="table-font-size px-2 py-0">
								<div class="d-flex align-center">
									<div class="d-flex align-center" style="min-width: 80px">
										<v-chip x-small hide-details :color="getColorStatus(item.status)">
											{{ getStatus(item.status) }}
										</v-chip>
									</div>
									<div>
										<span>{{ item.firstName }} {{ item.lastName }}</span>
									</div>
								</div>
							</td>
							<td style="height: 30px !important" class="table-font-size px-2 py-0">
								<v-chip label pill x-small color="primary"> {{ calculateAge(item.birthdate) }}</v-chip>
							</td>
							<td style="height: 30px !important" class="table-font-size px-2 py-0">
								{{ formatTimestamp(item.birthdate) }}
							</td>
							<td style="height: 30px !important" class="table-font-size px-2 py-0">
								{{ item.gender === 'M' ? $t('male') : $t('female') }}
							</td>
							<td style="height: 30px !important" class="text-right px-2">
								<v-icon @click.stop="addResidentPractitioners(item)" size="18" class="mr-1" color="primary">
									mdi mdi-eye
								</v-icon>
								<v-icon @click.stop="openDeleteDialog(item)" size="18" color="primary">mdi mdi-delete</v-icon>
							</td>
						</tr>
					</template>
				</v-data-table>
			</v-card>
		</v-col>
	</v-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { pccLogoBlack, patientBlack, logoDrawer } from '@/assets';
import UsersRepository from '@/data/repositories/v1/UsersRepository.js';
import {
	getDataTableFooterProps,
	calculateAge,
	getRowsForDataTable,
	showErrorAlert,
	showSuccessAlert,
	formatTimestamp,
} from '../../../utils/utilities';
import { ref } from 'vue';
import BirthdatePicker from '@/views/Client/components/BirthdatePicker.vue';

const rowRefs = ref({});

export default {
	components: {
		BirthdatePicker,
	},
	data () {
		return {
			dialog: false,
			deleteDialog: false,
			resident: null,
			patientToDelete: null,
			PatientBlack: patientBlack,
			loading: false,
			openDialog: false,
			selectedPccUser: null,
			selectedNotMappedUser: null,
			searchPccPatient: '',
			searchNotMappedPatient: '',
			searchMappedPatient: '',
			createResidentDialog: false,
			roleToBeCreated: null,
			loadingPractitionersHeaders: false,
			loadingUsersNeedToBeMapped: false,
			loadingNotMappedUsers: false,
			loadingMappedUsers: false,
			items: {
				notMappedUsers: [],
				mappedUsers: [],
				usersNeedToBeMapped: [],
			},
			pccHeaders: [
				{ text: this.$t('patient-name'), value: 'residentFirstName', class: 'px-0 py-0 pl-2 height15-black' },
				{ text: this.$t('age'), value: 'birthdate', class: 'px-0 py-0 pl-4  height15-black' },
				{ text: this.$t('birthdate'), value: 'residentBirthDate', class: 'px-0 py-0 pl-2 height15-black' },
				{ text: this.$t('gender'), value: 'residentGender', class: 'px-0 py-0 pl-2 height15-black' },
				{ text: '', value: '', class: 'px-0 py-0 height15-black', width: '50' },
			],
			staffHeaders: [
				{ text: this.$t('patient-name'), value: 'firstName', class: 'px-0 py-0 pl-2 height15-black', width: '400' },
				{ text: this.$t('age'), value: 'birthdate', class: 'px-0 py-0 pl-3 height15-black' },
				{ text: this.$t('birthdate'), value: 'birthdate', class: 'px-0 py-0 pl-2 height15-black' },
				{ text: this.$t('gender'), value: 'gender', class: 'px-0 py-0 pl-2 height15-black' },
				{ text: '', value: '', class: 'px-0 py-0 height15-black' },
			],
			notMappedPatientsHeaders: [
				{ text: this.$t('patient-name'), value: 'firstName', class: 'px-0 py-0 pl-2 height15-black' },
				{ text: this.$t('age'), value: 'birthdate', class: 'px-0 py-0 pl-3 height15-black' },
				{ text: this.$t('birthdate'), value: 'birthdate', class: 'px-0 py-0 pl-2 height15-black' },
				{ text: this.$t('gender'), value: 'gender', class: 'px-0 py-0 pl-2 height15-black' },
				{ text: '', value: '', class: 'px-0 py-0 height15-black', width: '50' },
			],
			practitionersHeaders: [
				{ text: this.$t('name'), value: 'firstName', class: 'align-center' },
				{ text: this.$t('phone'), value: 'cellPhone', class: 'align-center' },
				{ text: this.$t('provider-type'), value: 'providerType', class: 'align-center' },
				{ text: this.$t('role'), value: '', class: 'align-center', width: 150, sortable: false },
				{ text: '', value: '', class: 'align-center' },
			],
			highlightedId: null,
			tableHeight: '300px',
			getPractitionersResponse: [],

			// Create User/Resident data
			addressIsValid: true,
			userDetails: {
				firstName: '',
				lastName: '',
				email: 'generic@wellnessmetric.net',
				gender: 0,
			},
			dataStatus: null,
			loader: null,
			staffSelected: [],
			patientAddress: '',
			validationErrors: [],
			addressInfo: {
				address: '',
				zip: '',
				city: '',
				state: '',
				country: '',
				lat: 32.798718741846955,
				lng: -96.79917112574036,
			},
			smoker: '0',
			heightInCm: null,
			birthdate: '',
			weightInKg: null,
			tab: null,
			userDetailsPolicyNumber: '',
			emailError: false,
			scaleAndTempUnit: 'IMPERIAL',
			glucoseAndCholesterolUnit: 'mg/dl',
			patients: {
				gender: [
					{ text: 'Male', value: 1 },
					{ text: 'Female', value: 2 },
				],
			},
			phone: '+1 234 567 8900',
			timezone: null,
			enableRTM: false,
			enableRPM: false,
			enableCCM: false,
			selectedLocation: {},
			toggle: [],
			doctors: [],
			prescribingDoctor: null,
			mapDialogStep: 1,
		};
	},
	computed: {
		logoDrawer () {
			return logoDrawer;
		},
		pccLogoBlack () {
			return pccLogoBlack;
		},
		...mapState({
			userData: (state) => state.authentication.userData,
			selectedHospitalId: (state) => state.filterbar.hospitalId,
			medicalInstitution: (state) => state.filterbar.medicalInstitution,
			rateLimits: (state) => state.pcc.rateLimits,
		}),
		...mapGetters({
			roleName: 'authentication/getRole',
			userSettings: 'authentication/getUserSettings',
			getDoctorAndNurse: 'hospitals/getDoctorAndNurse',
			roles: 'roles/getRolesForForm',
			pageHeight: 'pageHeight',
		}),
		isSaveButtonDisabled () {
			return this.prescribingDoctor === null;
		},
	},
	created () {
		this.getPCCUsersMappings();
		window.addEventListener('resize', this.updateDataTableHeight);
	},
	async mounted () {
		setTimeout(() => {
			this.updateDataTableHeight();
		}, 100);

		this.getMedicalTeams();
		this.getSelectedLocation();
	},
	watch: {
		selectedHospitalId: {
			handler () {
				this.getPCCUsersMappings();
			},
			deep: true,
		},
		selectedPccUser (newVal, oldVal) {
			if (oldVal === null && newVal !== null) {
				this.updateDataTableHeight();
			}
		},
		selectedNotMappedUser (newVal, oldVal) {
			if (oldVal === null && newVal !== null) {
				this.updateDataTableHeight();
			}
		},
		resident (val) {
			this.loadingPractitionersHeaders = true;
			const locationId = Number(localStorage.getItem('selectedHospitalAssignedId'));

			this.getPractitioners({
				pccOrgId: val.pccCompanyId ?? val.pccOrganizationId,
				pccPatientId: val.pccUserId ?? val.id,
				locationId: locationId,
			}).then((res) => {
				this.getPractitionersResponse = res.data;
				this.loadingPractitionersHeaders = false;
			});
		},
		createResidentDialog (val) {
			if (!val) {
				this.mapDialogStep = 1;
				this.prescribingDoctor = null;
				this.enableRPM = false;
				this.enableRTM = false;
				this.enableCCM = false;
			}
		},
	},
	methods: {
		...mapActions({
			getPractitioners: 'pcc/getPractitioners',
			getMappedPccResidentsForHospital: 'pcc/getMappedPccResidentsForHospital',
			addMappedPccUser: 'pcc/addMappedPccUser',
			deleteMappedUser: 'pcc/deleteMappedUser',
		}),
		formatTimestamp,
		scrollToRow (desiredId) {
			const el = rowRefs.value[desiredId];
			if (el) {
				el.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});

				// set highlight
				this.highlightedId = desiredId;
			}
		},
		setRowRef (id, el) {
			if (el) {
				rowRefs.value[id] = el;
			}
		},
		clickResidentNeedToBeMap (item) {
			const suggestionWithNotMappedResident = this.items.notMappedUsers.find(
				(p) => p.id === this.selectedPccUser.residentMapSuggestion.localUserId,
			);

			this.selectedNotMappedUser = suggestionWithNotMappedResident;
			this.scrollToRow(suggestionWithNotMappedResident.residentMapSuggestion.localUserId);
		},
		getPCCUsersMappings () {
			switch (this.roleName) {
				case 'SYSTEM_ADMIN':
					if (this.selectedHospitalId !== null) {
						this.getUsersForMapping();
					}
					return;
				case 'SYSTEM_USER':
					if (this.selectedHospitalId !== null) {
						this.getUsersForMapping();
					}
					return;
				case 'HOSPITAL_ADMIN':
					this.getUsersForMapping();
					break;
				case 'DOCTOR':
					this.getUsersForMapping();
					break;
				case 'NURSE':
					this.getUsersForMapping();
					break;
			}
		},
		getColorStatus (status) {
			switch (status) {
				case 1:
					return 'background-green-dot-status';
				case 0:
					return 'grey';
				case 10:
					return 'background-red-dot-status';
				case 99:
					return 'background-black-dot-status';
			}
		},
		getStatus (status) {
			switch (status) {
				case 1:
					return this.$t('active');
				case 0:
					return this.$t('inactive');
				case -1:
					return this.$t('pending');
				case 10:
					return this.$t('deleted');
				case 99:
					return this.$t('deceased');
			}
		},
		calculateAge,
		getRowsForDataTable,
		getDataTableFooterProps,
		openDeleteDialog (item) {
			this.deleteDialog = true;
			this.patientToDelete = item;
		},
		confirmDeletion () {
			this.loading = true;
			this.deleteMappedUser(this.patientToDelete.id).then(() => {
				this.getUsersForMapping();
				this.searchPccPatient = '';
				this.searchNotMappedPatient = '';
				this.searchMappedPatient = '';
				this.selectedNotMappedUser = null;
				this.selectedPccUser = null;
				this.deleteDialog = false;
				this.loading = false;
			});
		},
		getStatusIcons (status) {
			switch (status) {
				case 'Active':
					return 'color-green';
				default:
					return '';
			}
		},
		confirmCreate () {
			UsersRepository.createResident(this.resident.id).then(() => {
				this.getUsersForMapping();
				this.dialog = false;
			});
		},
		createResident (item) {
			this.resident = item;

			this.userDetails.firstName = item.residentFirstName;
			this.userDetails.lastName = item.residentLastName;
			this.birthdate = item.residentBirthDate;
			this.userDetails.gender = item.residentGender === 'MALE' ? 1 : 2;
			this.selectedLocation = this.userData.hospitalsAssignedData.find(
				(s) => s.id === Number(localStorage.getItem('selectedHospitalAssignedId')),
			);
			this.createResidentDialog = true;
		},
		addResidentPractitioners (item) {
			this.resident = item;

			this.userDetails.firstName = item.residentFirstName;
			this.userDetails.lastName = item.residentLastName;
			this.birthdate = item.residentBirthDate;
			this.userDetails.gender = item.residentGender === 'MALE' ? 1 : 2;
			this.selectedLocation = this.userData.hospitalsAssignedData.find(
				(s) => s.id === Number(localStorage.getItem('selectedHospitalAssignedId')),
			);
			this.mapDialogStep = 2;
			this.createResidentDialog = true;
		},
		getUsersForMapping () {
			this.loadingNotMappedUsers = true;
			this.loadingUsersNeedToBeMapped = true;
			this.loadingMappedUsers = true;
			let selectedHospitalId = null;
			if (this.roleName === 'HOSPITAL_ADMIN') {
				selectedHospitalId = this.userData.hospitalAssignedData.id;
			} else if (this.roleName === 'DOCTOR' || this.roleName === 'NURSE') {
				selectedHospitalId = Number(localStorage.getItem('selectedHospitalAssignedId')) || 0;
			} else {
				selectedHospitalId = this.selectedHospitalId;
			}

			this.getMappedPccResidentsForHospital(selectedHospitalId).then((response) => {
				this.items = response.data || null;

				this.loadingNotMappedUsers = false;
				this.loadingUsersNeedToBeMapped = false;
				this.loadingMappedUsers = false;
			});
		},
		mapUser () {
			this.loading = true;
			let selectedHospitalId = null;
			let selectedOrganizationId = null;

			if (this.roleName === 'HOSPITAL_ADMIN') {
				selectedHospitalId = this.userData.hospitalAssignedData.id;
				selectedOrganizationId = this.userData.organizationId;
			} else if (this.roleName === 'DOCTOR' || this.roleName === 'NURSE') {
				selectedHospitalId = Number(localStorage.getItem('selectedHospitalAssignedId')) || 0;
				selectedOrganizationId = this.userData.organizationId;
			} else {
				selectedHospitalId = this.selectedHospitalId;
				selectedOrganizationId = this.medicalInstitution;
			}

			const body = {
				pccUserId: this.selectedPccUser?.residentId || 'string',
				pccLocationId: this.selectedPccUser?.pccLocationId || null,
				mappedUserId: this.selectedNotMappedUser?.id || 0,
				mappedHospitalId: selectedHospitalId,
				mappedOrganizationId: selectedOrganizationId,
				firstName: this.selectedPccUser?.residentFirstName,
				lastName: this.selectedPccUser?.residentLastName,
				birthdate: this.selectedPccUser?.residentBirthDate,
			};

			this.addMappedPccUser(body).then((response) => {
				if (response.resFlag) {
					this.getUsersForMapping();
					this.selectedNotMappedUser = null;
					this.selectedPccUser = null;
					this.searchPccPatient = '';
					this.searchNotMappedPatient = '';
					this.searchMappedPatient = '';
				}
				this.loading = false;
			});
		},
		async createMedicalStaffFromPcc (userIndex) {
			const selectedUser = this.getPractitionersResponse[userIndex];

			const selectedHospitalId = Number(localStorage.getItem('selectedHospitalAssignedId'));
			this.selectedLocation = this.userData.hospitalsAssignedData.find((it) => it.id === selectedHospitalId);

			const emailAndUsername =
				selectedUser.email || this.generateUserCode(selectedUser.firstName, selectedUser.lastName);
			const body = {
				firstName: selectedUser.firstName,
				lastName: selectedUser.lastName,
				phone: selectedUser.officePhone?.split(' ').join(''),
				email: emailAndUsername,
				address: selectedUser.addressLine1,
				zip: selectedUser.postalCode,
				city: selectedUser.city,
				state: selectedUser.state,
				country: selectedUser.country,
				addressLatitude: this.selectedLocation.addressLatitude,
				addressLongitude: this.selectedLocation.addressLongitude,
				username: emailAndUsername,
				roleId: selectedUser?.role || 3,
				hospitalId: this.selectedLocation.id,
				userTimezone: this.selectedLocation.hospitalTimezone,
				isSequoiaUser: false,
			};
			await this.$store.dispatch('users/createUser', body).then((res) => {
				if (res.resFlag === true) {
					let selectedOrganizationId = null;
					let selectedHospitalId = Number(localStorage.getItem('selectedHospitalAssignedId'));

					if (this.roleName === 'HOSPITAL_ADMIN') {
						selectedHospitalId = this.userData.hospitalAssignedData.id;
						selectedOrganizationId = this.userData.organizationId;
					} else if (this.roleName === 'DOCTOR' || this.roleName === 'NURSE') {
						selectedHospitalId = Number(localStorage.getItem('selectedHospitalAssignedId')) || 0;
						selectedOrganizationId = this.userData.organizationId;
					} else {
						selectedHospitalId = this.selectedHospitalId;
						selectedOrganizationId = this.medicalInstitution;
					}

					const mapBody = {
						pccUserId: selectedUser.practitionerId,
						pccLocationId: this.resident.pccLocationId,
						mappedUserId: res.data.id,
						mappedHospitalId: selectedHospitalId,
						mappedOrganizationId: selectedOrganizationId,
						firstName: res.data.firstName,
						lastName: res.data.lastName,
					};

					this.addMappedPccUser(mapBody).then((mapRes) => {
						if (!mapRes.resFlag) {
							showErrorAlert(mapRes.msg);
							return;
						}

						this.loadingPractitionersHeaders = true;
						const locationId = Number(localStorage.getItem('selectedHospitalAssignedId'));

						this.getPractitioners({
							pccOrgId: this.resident.pccCompanyId ?? this.resident.pccOrganizationId,
							pccPatientId: this.resident.pccUserId ?? this.resident.id,
							locationId: locationId,
						}).then((res) => {
							this.getPractitionersResponse = res.data;
							this.loadingPractitionersHeaders = false;
						});
					});
				}
			});
		},
		updateDataTableHeight () {
			this.$nextTick(() => {
				if (this.$refs.pccUsersTable) {
					this.tableHeight = this.$refs.pccUsersTable.$el.clientHeight - 109 + 'px';
				}
			});
		},

		onLocationSelect (selected) {
			this.selectedLocation = selected;
			this.patientAddress = selected.address;
			this.addressInfo.city = selected.city;
			this.addressInfo.state = selected.state;

			this.addressInfo.zip = selected.zip;
			this.addressInfo.country = selected.country;
		},

		async getMedicalTeams () {
			const currentHospital = localStorage.getItem('selectedHospitalAssignedId');
			await this.$store.dispatch('hospitals/getRelatedMedicalStaffForHospital', Number(currentHospital));
			this.doctors = this.getDoctorAndNurse.filter((d) => d.status === 1 && d.roleId === 3);
			this.staffSelected = this.getDoctorAndNurse.map((d) => d.id);
			this.loading = false;
		},
		async createPatient () {
			if (
				this.userDetails.firstName.length === 0 ||
				this.userDetails.lastName.length === 0 ||
				this.birthdate.length === 0 ||
				this.userDetails.gender < 1
			) {
				showErrorAlert(this.$t('fill-all-fields-warning'));
				return;
			}

			const bodyForPatientCreate = {
				firstName: this.userDetails.firstName,
				lastName: this.userDetails.lastName,
				phone: '+1 234 567 8900',
				email: this.userDetails.email,
				address: this.addressInfo.address,
				zip: this.addressInfo.zip,
				city: this.addressInfo.city,
				state: this.addressInfo.state,
				country: this.addressInfo.country,
				addressLatitude: this.addressInfo.lat,
				addressLongitude: this.addressInfo.lng,
				username: this.userDetails.email,
				gender: this.userDetails.gender === 1 ? 'M' : 'F',
				birthdate: this.birthdate,
				roleId: 5, // PATIENT
				language: this.userDetails.language,
				userTimezone: this.addressIsValid ? this.timezone : this.timezone.timeZoneId,
				userSettingsJson: JSON.stringify(this.getPreferences()),
				IsManualMeasurement: this.dataStatus,
				hospitalId: localStorage.getItem('selectedHospitalAssignedId'),
				isEmailAccountProvided: false,
				isRpmEnabled: this.enableRPM,
				isRtmEnabled: this.enableRTM,
				isCcmEnabled: this.enableCCM,
				healthInsurance: 0,
			};

			await this.$store.dispatch('users/createUser', bodyForPatientCreate).then(async (res) => {
				if (res.resFlag === true) {
					// handle patient metadata
					const metaDataBody = {
						userId: res.data.id,
						heightInCm: 0.0,
						weightInKg: 0.0,
						ssn: '',
						medicalRegistrationNumber: '',
						healthInsurance: 0,
						memberIdPolicyNumber: '',
						isCigaretteSmoker: false,
						alcoolConsumtion: 0,
						prescriptions: '',
						metadataInfo: '',
						isManualMeasurement: false,
					};
					await this.$store.dispatch('patientmetadata/editPatientMetaData', metaDataBody);

					// handle medical team
					await this.manageMedicalTeam(res.data);

					const requestSetStatus = {
						userId: res.data.id,
						statusValueToSet: res.data.status,
					};
					await this.$store.dispatch('users/setUserStatus', requestSetStatus);
					showSuccessAlert(this.$t('successfully-created-patient'));
					this.mapDialogStep = 2;
					this.selectedPccUser = this.resident;
					this.selectedNotMappedUser = { id: res.data.id };
					this.prescribingDoctor = null;
					this.mapUser();
				} else {
					showErrorAlert(res.msg);
				}
			});
		},
		async manageMedicalTeam (res) {
			if (this.prescribingDoctor !== null) {
				const body = {
					hospitalId: localStorage.getItem('selectedHospitalAssignedId'),
					patientId: res.id,
					primaryDoctorId: this.prescribingDoctor.id,
					medicalTeamTitle: res.firstName + ' ' + res.lastName + ' Team',
					medicalTeamDescription: res.firstName + ' ' + res.lastName,
					status: 1,
				};
				await this.$store.dispatch('medicalteam/createMedicalTeam', body).then((res) => {
					const createdMedicalTeamId = res.id;
					this.assignMemberToMedicalTeam(createdMedicalTeamId);
				});
			} else {
				const body = {
					hospitalId: localStorage.getItem('selectedHospitalAssignedId'),
					patientId: res.id,
					medicalTeamTitle: res.firstName + ' ' + res.lastName + ' Team',
					medicalTeamDescription: res.firstName + ' ' + res.lastName,
					status: 1,
				};
				await this.$store.dispatch('medicalteam/createMedicalTeam', body);
			}
		},

		async assignMemberToMedicalTeam (medicalTeamId) {
			const bodyAssign = {
				medicalTeamId: medicalTeamId,
				memberUserIdList: this.staffSelected,
			};
			await this.$store.dispatch('medicalteam/assignMultipleMembersToMedicalTeam', bodyAssign);
			this.$emit('false');
		},
		getSelectedLocation () {
			var selectedHospitalId = localStorage.getItem('selectedHospitalAssignedId');
			this.selectedLocation = this.userData.hospitalsAssignedData.find((it) => it.id === Number(selectedHospitalId));
			this.onLocationSelect(this.selectedLocation);
		},
		getPreferences () {
			const preferences = {
				Units: this.scaleAndTempUnit,
				Concentration: this.glucoseAndCholesterolUnit,
			};
			return preferences;
		},
		getRoleNameById (roleId) {
			return this.roles.find((r) => r.id === roleId)?.roleNameForDisplay || '';
		},
		generateUserCode (firstName, lastName) {
			const firstInitial = firstName.trim().charAt(0).toLowerCase();
			const lastInitial = lastName.trim().charAt(0).toLowerCase();

			// Generate 6 random digits
			let randomNumbers = '';
			for (let i = 0; i < 6; i++) {
				randomNumbers += Math.floor(Math.random() * 10); // 0-9
			}

			return firstInitial + lastInitial + randomNumbers + '@wellnessmetric.net';
		},
	},
};
</script>

<style lang="scss">
.v-messages {
	display: none !important;
}

.small i {
	font-size: 18px !important;
}

.small [class*='__ripple'] {
	left: 0;
	display: none;
}

.highlight-row {
	animation: blink-bg 1s ease-in-out infinite;
}

@keyframes blink-bg {
	0%,
	100% {
		background-color: transparent;
	}

	50% {
		background-color: #d1d1d1;
	}
}
</style>
